import { Box, Divider, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useContext } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useLocalization } from '@/hooks/useLocalization';
import {
  useGetRealizedGainsSummaryQuery,
  useGetHoldingRealizedGainsSummaryQuery,
} from '@/store/holdingsApi';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import { ThemeContext } from '@/contexts/themeContext';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';
import { CoolBars } from './CoolBars';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatAppDate } from '@/utils/date';
import { GainsLossLoadingSkeletons } from './GainsLossLoadingSkeletons';
import { useGetAdvisorGroupSettingsQuery } from '@/store/settingsApi';
import AdvisorGroupSettings from '@/utils/advisorGroupSettings.utility';

interface RealizedGainLossSummaryProps {
  startDate?: string;
  endDate?: string;
  setStartDate: (date: string | undefined) => void;
  setEndDate: (date: string | undefined) => void;
  holdingId?: number;
  accountId?: number;
  investorId?: number;
}

const RealizedGainLossSummary = ({
  startDate,
  endDate,
  setStartDate: _setStartDate,
  setEndDate: _setEndDate,
  holdingId,
  accountId,
  investorId,
}: RealizedGainLossSummaryProps) => {
  const theme = useTheme();
  const authLoaded = useAuthIsLoaded();
  const { t } = useLocalization();
  const { brandColors } = useContext(ThemeContext);

  const { data: advisorGroupSettings, isLoading: advisorGroupSettingsLoading } =
    useGetAdvisorGroupSettingsQuery(authLoaded ? undefined : skipToken);

  const effectiveExclusions = useEffectiveExclusions();
  const serializedExcludedAccounts =
    effectiveExclusions.excludedAccounts.join(',');
  const serializedExcludedAccountTypes =
    effectiveExclusions.excludedAccountTypes.join(',');
  const serializedExcludedCategories =
    effectiveExclusions.excludedCategories.join(',');

  const shouldUseBrandColors =
    brandColors.hasPrimaryAndSecondary &&
    typeof brandColors.primary === 'string' &&
    typeof brandColors.secondary === 'string';

  const proceedsColor =
    shouldUseBrandColors && brandColors.primary
      ? brandColors.primary
      : '#6FDD98';
  const costBasisColor =
    shouldUseBrandColors && brandColors.secondary
      ? brandColors.secondary
      : 'rgba(148, 186, 212, 0.56)';

  // Get default date range for the current year
  const { startDate: initialStartDate, endDate: initialEndDate } =
    getDateRangeFromFilter(DataRangeFilterEnum.OpenYear);

  // Get realized gains summary data
  const { data: portfolioSummary, isLoading: portfolioSummaryLoading } =
    useGetRealizedGainsSummaryQuery(
      authLoaded && !holdingId
        ? {
            sort: [{ property: 'PercentChange', direction: 'desc', order: 1 }],
            InvestorId: investorId,
            AccountId: accountId,
            ...(serializedExcludedAccounts
              ? { ExcludedAccounts: serializedExcludedAccounts }
              : {}),
            ...(serializedExcludedAccountTypes
              ? { ExcludedAccountTypes: serializedExcludedAccountTypes }
              : {}),
            ...(serializedExcludedCategories
              ? { ExcludedCategories: serializedExcludedCategories }
              : {}),
          }
        : skipToken,
    );

  const { data: holdingSummary, isLoading: holdingSummaryLoading } =
    useGetHoldingRealizedGainsSummaryQuery(
      authLoaded && holdingId
        ? {
            holdingId,
            startDate: startDate || initialStartDate,
            endDate: endDate || initialEndDate,
            sort: [{ property: 'PercentChange', direction: 'desc', order: 1 }],
            investorId,
          }
        : skipToken,
    );

  const summaryData = holdingId ? holdingSummary : portfolioSummary;
  const isSummaryLoading = holdingId
    ? holdingSummaryLoading
    : portfolioSummaryLoading;

  const isLoadingState =
    !authLoaded || isSummaryLoading || advisorGroupSettingsLoading;

  const shortTermValue = summaryData?.ShortTerm ?? 0;
  const longTermValue = summaryData?.LongTerm ?? 0;
  const shortTermColor =
    shortTermValue < 0 ? theme.palette.error.main : theme.palette.success.main;
  const longTermColor =
    longTermValue < 0 ? theme.palette.error.main : theme.palette.success.main;

  const proceeds = summaryData?.SalePrice ?? 0;
  const costBasis = summaryData?.CostBasis ?? 0;

  const bars = [
    {
      name: t('proceeds'),
      value: proceeds,
      color: proceedsColor,
    },
    {
      name: t('grid.costBasis'),
      value: costBasis,
      color: costBasisColor,
    },
  ];

  const summaryItems = [
    { name: t('shortTerm'), value: shortTermValue, color: shortTermColor },
    { name: t('longTerm'), value: longTermValue, color: longTermColor },
  ];

  const isDisclaimerVisible =
    AdvisorGroupSettings(advisorGroupSettings).getByKey('ShowCCTMessages');

  // Note: Date range handlers provided by parent via props; not used directly here
  // Note: Date range selector is controlled by parent widget header in this view

  if (isLoadingState) {
    return (
      <Box sx={{ mb: 4 }}>
        <GainsLossLoadingSkeletons repeat={2} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Grid
          container
          spacing={{ xs: 4, md: 8 }}
        >
          <Grid size={{ xs: 10, sm: 6, md: 4 }}>
            <Box>
              <Stack
                direction='column'
                flex={1}
                gap={2}
              >
                <Typography
                  fontSize={16}
                  fontWeight={600}
                >
                  {t('gainsLosses.summarySinceInception')}
                </Typography>

                <CoolBars barConfigs={bars} />
              </Stack>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box>
              <Stack
                direction='column'
                flex={1}
                gap={2}
              >
                <Typography
                  fontSize={16}
                  fontWeight={600}
                >
                  {t('summary')} ({formatAppDate(startDate || initialStartDate)}{' '}
                  - {formatAppDate(endDate || initialEndDate)})
                </Typography>

                <Stack
                  flex={1}
                  gap={2}
                  divider={
                    <Divider
                      orientation='horizontal'
                      flexItem
                    />
                  }
                >
                  {summaryItems.map((summaryItem) => {
                    return (
                      <Stack
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        justifyContent='space-between'
                        flexWrap='wrap'
                      >
                        <Typography
                          variant='body2'
                          sx={{ flexShrink: 0 }}
                        >
                          {summaryItem.name}:{' '}
                        </Typography>
                        <Typography
                          variant='body2'
                          color={summaryItem.color}
                          sx={{
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {formatNumberToCurrency(summaryItem.value)}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {isDisclaimerVisible && (
        <Typography
          variant='caption'
          sx={{ color: theme.palette.text.secondary }}
        >
          {t('gainsLosses.realizedDisclaimer')}
        </Typography>
      )}
    </Box>
  );
};

export default RealizedGainLossSummary;
